import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthModal } from '../../contexts/AuthModalContext'
import { useSidebar } from '../../contexts/SidebarContext'
import { useAuth } from '../../hooks/useAuth'
import { getProfileUrl } from '../../utils/url'
import Button from '../common/Button'
import Logo from '../common/Logo'

interface NavbarProps {
  transparent?: boolean
}

export default function Navbar({ transparent = false }: NavbarProps) {
  const { isAuthenticated, user, logout } = useAuth()
  const { openAuthModal } = useAuthModal()
  const { toggle: toggleSidebar } = useSidebar()
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Check if we're on the landing page or on an authenticated route
  const isLandingPage = location.pathname === '/'
  const isAuthenticatedRoute = ['/home', '/edit', '/account'].includes(location.pathname)

  const handleLogout = async () => {
    await logout()
    navigate('/')
  }

  const navClasses = transparent
    ? 'absolute top-0 left-0 right-0 z-50 bg-transparent'
    : 'bg-white/80 backdrop-blur-md border-b border-black/10 sticky top-0 z-50'

  return (
    <nav className={navClasses}>
      <div className="w-full px-4 sm:px-8 lg:px-12 pt-4">
        <div className="flex justify-between items-center h-16">
          {/* Left side - Logo and Hamburger */}
          <div className="flex items-center gap-3">
            {isAuthenticated && isAuthenticatedRoute && (
              <button
                onClick={toggleSidebar}
                className="lg:hidden p-2 -ml-2 rounded-lg text-black/60 hover:text-black hover:bg-black/5 transition-colors"
                aria-label="Toggle sidebar"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            )}
            {/* Hide logo on landing page for guests */}
            {!(isLandingPage && !isAuthenticated) && (
              <Logo size="lg" to={isAuthenticated ? '/home' : '/'} />
            )}
          </div>

          {/* Right side - Navigation */}
          <div className="flex items-center">
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              {isAuthenticated ? (
                <>
                  {user?.has_profile && user.profile_username && (
                    <Button
                      as="a"
                      href={getProfileUrl(user.profile_username)}
                      external
                      variant="secondary"
                      size="sm"
                      icon={
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      }
                      iconPosition="right"
                    >
                      View Profile
                    </Button>
                  )}
                  <div className="h-6 w-px bg-black/10" />
                  <Button variant="ghost" size="sm" onClick={handleLogout}>
                    Sign out
                  </Button>
                </>
              ) : isLandingPage ? (
                <button
                  onClick={() => openAuthModal('signin')}
                  className="px-4 py-2 bg-white/70 backdrop-blur-xl shadow-md rounded-lg text-black font-medium hover:bg-white/80 transition-colors text-sm"
                >
                  Sign in
                </button>
              ) : (
                <Button as="link" to="/login" variant="secondary" size="sm">
                  Sign in
                </Button>
              )}
            </div>

            {/* Mobile menu button - only show on non-authenticated routes or landing */}
            {(!isAuthenticated || !isAuthenticatedRoute) && (
              <div className="md:hidden flex items-center">
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="p-2 rounded-lg text-black/60 hover:text-black hover:bg-black/5 transition-colors"
                >
                  {mobileMenuOpen ? (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6h16M4 12h16M4 18h16"
                      />
                    </svg>
                  )}
                </button>
              </div>
            )}

            {/* Mobile - Show sign out for authenticated routes */}
            {isAuthenticated && isAuthenticatedRoute && (
              <div className="md:hidden flex items-center gap-2">
                {user?.has_profile && user.profile_username && (
                  <a
                    href={getProfileUrl(user.profile_username)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg text-black/60 hover:text-black hover:bg-black/5 transition-colors"
                    title="View Profile"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </a>
                )}
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-lg text-black/60 hover:text-black hover:bg-black/5 transition-colors"
                  title="Sign out"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Navigation - for non-authenticated routes */}
        {mobileMenuOpen && (!isAuthenticated || !isAuthenticatedRoute) && (
          <div
            className={`md:hidden py-4 border-t ${transparent ? 'bg-white/95 backdrop-blur-md rounded-b-xl border-black/10' : 'border-black/10'}`}
          >
            <div className="flex flex-col gap-3">
              {isAuthenticated ? (
                <>
                  {user?.has_profile && user.profile_username && (
                    <Button
                      as="a"
                      href={getProfileUrl(user.profile_username)}
                      external
                      variant="ghost"
                      size="sm"
                      className="justify-start mx-2"
                      onClick={() => setMobileMenuOpen(false)}
                      icon={
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      }
                      iconPosition="right"
                    >
                      View Profile
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="justify-start mx-2"
                    onClick={() => {
                      handleLogout()
                      setMobileMenuOpen(false)
                    }}
                  >
                    Sign out
                  </Button>
                </>
              ) : isLandingPage ? (
                <button
                  onClick={() => {
                    openAuthModal('signin')
                    setMobileMenuOpen(false)
                  }}
                  className="mx-2 px-4 py-2 bg-white/70 backdrop-blur-xl shadow-md rounded-lg text-black font-medium hover:bg-white/80 transition-colors text-sm"
                >
                  Sign in
                </button>
              ) : (
                <Button
                  as="link"
                  to="/login"
                  variant="secondary"
                  size="sm"
                  className="mx-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sign in
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}
